import {
	FeedbackReport,
	FeedbackReportAuthorKind,
	FeedbackReportBoard,
	FeedbackReportBoardSource,
	FeedbackReportBuild,
	FeedbackReportComment,
	FeedbackReportEnv,
	FeedbackReportKind,
	FeedbackReportLegacyStatus,
	FeedbackReportListRow,
	FeedbackReportOrigin,
	FeedbackReportOriginKind,
	FeedbackReportPriority,
	FeedbackReportSource,
	FeedbackReportStatus,
	FEEDBACK_REPORT_CLOSED_STATUSES,
	FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
	FEEDBACK_REPORT_STATUSES,
	FEEDBACK_REPORT_TAG_MAX_LENGTH,
	FEEDBACK_REPORT_TAG_PATTERN,
	feedbackReportStatus,
} from '@run-diffusion/shared';

// The Feedback admin page's rules, apart from the page: what each status, kind and priority is
// called, which reports each tab and filter shows, how they sort, what each move asks for, and
// the report as text for a developer's agent. The page changes reports only through restServer
// (./feedbackWrites.ts), whose feedback service decides what may change (ADR 0042).

export const STATUS_LABELS: Record<FeedbackReportStatus, string> = {
	new: 'New',
	triaged: 'Triaged',
	needs_human: 'Needs human',
	ready_for_agent: 'Ready for agent',
	in_progress: 'In progress',
	in_pr: 'In PR', // the fix is in a pull request
	qa_test: 'QA test', // live, waiting on a tester
	done: 'Done',
	wontfix: 'Won\'t fix',
	duplicate: 'Duplicate',
};

// A report's status in today's ids: one migrated late, or a status line written before ADR 0042,
// may still carry an old one.
export const statusOf = (report: FeedbackReport): FeedbackReportStatus => feedbackReportStatus(report.status) || 'new';

// What any status id is called, old ids included (a status line keeps the id it was written with).
export const statusLabel = (status: FeedbackReportStatus | FeedbackReportLegacyStatus | string | null | undefined): string => {
	const current: FeedbackReportStatus | null = feedbackReportStatus(status);
	return current ? STATUS_LABELS[current] : '?';
};

export const isClosed = (report: FeedbackReport): boolean => FEEDBACK_REPORT_CLOSED_STATUSES.includes(statusOf(report));

export const KIND_LABELS: Record<FeedbackReportKind, string> = {
	bug: 'Bug',
	confusing: 'Confusing',
	idea: 'Idea',
};

// The board asks each kind its own two questions (web/board/src/ui/feedback/FeedbackDialog.vue);
// happened and expected hold the answers. These head them, in the third person.
export const KIND_QUESTIONS: Record<FeedbackReportKind, { happened: string, expected: string }> = {
	bug: { happened: 'What happened', expected: 'What they expected' },
	confusing: { happened: 'What they were trying to do', expected: 'What got in the way' },
	idea: { happened: 'What they would like', expected: 'What it would help them do' },
};

// Where the tester opened Feedback from on the board, or that a developer's agent filed it, or
// that it was imported from a list.
export const SOURCE_LABELS: Record<FeedbackReportSource, string> = {
	button: 'Feedback button',
	menu: 'menu',
	key: 'shortcut key',
	agent: 'agent',
	import: 'list',
	page: 'Feedback page',
};

const BOARD_SOURCES: FeedbackReportBoardSource[] = ['button', 'menu', 'key'];

// A report a developer's agent filed ('agent') or imported from a list ('import'), or null for
// one a tester sent from a board. Its origin says which; source says the same, should it lack one.
export const originKind = (report: FeedbackReport): FeedbackReportOriginKind | null => {
	if (report.origin?.kind) return report.origin.kind;
	return (BOARD_SOURCES as FeedbackReportSource[]).includes(report.source) ? null : report.source as FeedbackReportOriginKind;
};

// The badge on a report an agent filed or imported, or a person filed on the page.
export const ORIGIN_LABELS: Record<FeedbackReportOriginKind, string> = {
	agent: 'Agent',
	import: 'Imported',
	page: 'Filed here',
};

// How it arrived, as "sent 3 hours ago from the board's Feedback button", "filed 3 hours ago" (by
// the agent or person the page names beside it) or "imported 3 hours ago from Boards v2 sheet".
export const arrival = (report: FeedbackReport): { verb: string, via: string } => {
	const kind: FeedbackReportOriginKind | null = originKind(report);
	if (kind === 'agent') return { verb: 'filed', via: '' };
	if (kind === 'page') return { verb: 'filed', via: 'on this page' };
	if (kind === 'import') return { verb: 'imported', via: report.origin?.list ? `from ${report.origin.list}` : 'from a list' };
	return { verb: 'sent', via: `from the board's ${SOURCE_LABELS[report.source] || SOURCE_LABELS.button}` };
};

// A link the page may open: http(s) only, so a list's text can't become a script link.
export const httpLink = (value: string | null | undefined): string | null => {
	const link: string = (value || '').trim();
	return /^https?:\/\/\S+$/i.test(link) ? link : null;
};

// The screen it was sent from, unless unknown: an agent that names no viewport sends 0x0.
export const hasViewport = (env: FeedbackReportEnv | null | undefined): env is FeedbackReportEnv =>
	!!env && env.viewport?.w > 0 && env.viewport?.h > 0;

// What a report knows of where it was sent from. One an agent filed or imported comes from no
// board, and may not know its build or browser: the feedback API writes empty placeholders then
// (a board with no id, a build with no version or commit, an environment with no browser), so a
// page that dereferences them still draws it, and reports filed before it did hold null. Either
// way, it is absent, as the feedback API reads it too (has_board, has_build and has_env in
// restServer apiv2/feedback_service.py). A board always names the browser, if only as "Unknown
// browser".
export const hasBoard = (board: FeedbackReportBoard | null | undefined): board is FeedbackReportBoard =>
	!!board?.id;

export const hasBuild = (build: FeedbackReportBuild | null | undefined): build is FeedbackReportBuild =>
	!!build && !!(build.version || build.commit);

export const hasEnv = (env: FeedbackReportEnv | null | undefined): env is FeedbackReportEnv =>
	!!env?.browser;

export const PRIORITY_LABELS: Record<FeedbackReportPriority, string> = {
	p0: 'P0 Urgent',
	p1: 'P1 High',
	p2: 'P2 Normal',
	p3: 'P3 Low',
};

// What a status asks for before it is set: a question, a reason, the report it repeats, or the
// pull request. The note lands on the status change's line in the comments.
export interface StatusNoteCopy {
	title: string
	help: string
	noteLabel: string
	confirm: string
	noteRequired: boolean
}

export const STATUS_NOTE_COPY: Partial<Record<FeedbackReportStatus, StatusNoteCopy>> = {
	needs_human: {
		title: 'Ask a person',
		help: 'Say what needs a person: a question, a decision, an approval. It waits in Needs human until someone answers.',
		noteLabel: 'What needs a person',
		confirm: 'Ask',
		noteRequired: true,
	},
	wontfix: {
		title: 'Close as won\'t fix',
		help: 'Nobody will work on it. Say why, so the next person to read it knows.',
		noteLabel: 'Why not (optional)',
		confirm: 'Close it',
		noteRequired: false,
	},
	duplicate: {
		title: 'Close as a duplicate',
		help: 'Point it at the report it repeats. Work happens there.',
		noteLabel: 'A note (optional)',
		confirm: 'Close it',
		noteRequired: false,
	},
	in_pr: {
		title: 'Move to In PR',
		help: 'The fix is in a pull request. Link it, and say what changed.',
		noteLabel: 'What changed (optional)',
		confirm: 'Move it',
		noteRequired: false,
	},
	qa_test: {
		title: 'Ready to test',
		help: 'The fix is live. Say which build or deploy it went out in, so testers know what to check.',
		noteLabel: 'Build or deploy (optional)',
		confirm: 'Move it',
		noteRequired: false,
	},
	done: {
		title: 'Mark done',
		help: 'It works: tested, or shipped with nothing to test.',
		noteLabel: 'A note (optional)',
		confirm: 'Mark done',
		noteRequired: false,
	},
};

export const statusNeedsNote = (status: FeedbackReportStatus): boolean => !!STATUS_NOTE_COPY[status];

// The page's tabs: what waits on a person, what waits on a tester, what nobody has decided yet, the
// agents' work, and closed reports. The feedback API's views are the same
// (restServer apiv2/feedback_service.py).
export type FeedbackView = 'needs_human' | 'qa_test' | 'new' | 'agent' | 'closed' | 'all';

export const VIEW_STATUSES: Record<Exclude<FeedbackView, 'all'>, FeedbackReportStatus[]> = {
	needs_human: ['needs_human'],
	qa_test: ['qa_test'],
	new: ['new', 'triaged'],
	agent: ['ready_for_agent', 'in_progress', 'in_pr'],
	closed: ['done', 'wontfix', 'duplicate'],
};

export const VIEW_LABELS: Record<FeedbackView, string> = {
	needs_human: 'Needs human',
	qa_test: 'QA test',
	new: 'New',
	agent: 'Agent work',
	closed: 'Closed',
	all: 'All',
};

// What the page keeps listening to in full: every report outside Closed. Closed ones are kept
// forever and outgrow any list, so only the newest of them are read. (A Firestore `in` query takes
// at most 10 ids in this SDK, so the old ids aren't listed: manage.py migrate_feedback_lifecycle
// runs before this page deploys, and a report it missed still shows among the newest.)
export const LIVE_STATUSES: FeedbackReportStatus[] = FEEDBACK_REPORT_STATUSES.filter((status: FeedbackReportStatus) => !VIEW_STATUSES.closed.includes(status));

export const inView = (report: FeedbackReport, view: FeedbackView): boolean =>
	view === 'all' || VIEW_STATUSES[view].includes(statusOf(report));

// One status, on top of the tab.
export type StatusFilter = 'all' | FeedbackReportStatus;

export const matchesStatus = (report: FeedbackReport, filter: StatusFilter): boolean => filter === 'all' || statusOf(report) === filter;

// Tags: free-form labels that group reports (a release, an area), compared without case.
export const tagsOf = (report: FeedbackReport): string[] => (Array.isArray(report.triage?.tags) ? report.triage.tags.filter((tag: unknown) => typeof tag === 'string') : []);

export const matchesTag = (report: FeedbackReport, tag: string | null): boolean =>
	!tag || tagsOf(report).some((t: string) => t.toLowerCase() === tag.toLowerCase());

// Every tag in use, the first spelling of each, A to Z.
export const allTags = (reports: FeedbackReport[]): string[] => {
	const seen: Map<string, string> = new Map();
	reports.forEach((report: FeedbackReport) => tagsOf(report).forEach((tag: string) => {
		if (!seen.has(tag.toLowerCase())) seen.set(tag.toLowerCase(), tag);
	}));
	return Array.from(seen.values()).sort((a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

// A tag as the API keeps it (feedback_service.clean_tags): trimmed, spaces collapsed; null when
// it isn't one.
export const cleanTag = (value: string | null | undefined): string | null => {
	const tag: string = (value || '').replace(/\s+/g, ' ').trim();
	return tag && tag.length <= FEEDBACK_REPORT_TAG_MAX_LENGTH && FEEDBACK_REPORT_TAG_PATTERN.test(tag) ? tag : null;
};

// Who sent it: a RunDiffusion admin, or anyone else. Reports from before the flag was kept count as users'.
export type ReporterFilter = 'all' | 'admin' | 'user';

export const isAdminReport = (report: FeedbackReport): boolean => report.reporter?.isAdmin === true;

export const matchesReporter = (report: FeedbackReport, filter: ReporterFilter): boolean =>
	filter === 'all' || (filter === 'admin') === isAdminReport(report);

export type KindFilter = 'all' | FeedbackReportKind;

export const matchesKind = (report: FeedbackReport, filter: KindFilter): boolean => filter === 'all' || report.kind === filter;

// A number ("12" or "#12") finds that report, and "12" also finds an imported row whose id in
// its list is 12. A list row's id ("B12") finds its report, whole; anything else searches its
// words, board, reporter, company, tags, and the list it came from.
export const matchesSearch = (report: FeedbackReport, search: string): boolean => {
	const term: string = (search || '').trim().toLowerCase();
	if (!term) return true;
	const externalIds: string[] = [report.origin?.externalId, report.listRow?.externalId].map((id) => (id || '').trim().toLowerCase()).filter(Boolean);
	if (externalIds.includes(term)) return true;
	if (/^#?\d+$/.test(term)) return String(report.number) === term.replace('#', '');
	const board: FeedbackReportBoard | null = hasBoard(report.board) ? report.board : null;
	return [
		report.happened,
		report.expected,
		board?.title,
		board?.id,
		report.reporter?.email,
		report.reporter?.companyName,
		report.origin?.list,
		report.listRow?.list,
		...tagsOf(report),
	].some((value: string | null | undefined) => (value || '').toLowerCase().includes(term));
};

export type FeedbackSort = 'newest' | 'oldest' | 'priority';

const PRIORITY_RANK: Record<FeedbackReportPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
// Unset sorts after p3.
const priorityRank = (report: FeedbackReport): number => {
	const priority: FeedbackReportPriority | null | undefined = report.triage?.priority;
	return priority ? PRIORITY_RANK[priority] : 4;
};

// Priority puts the most urgent first, unset last, and the newest first within each.
export const compareReports = (sort: FeedbackSort) => (a: FeedbackReport, b: FeedbackReport): number => {
	if (sort === 'oldest') return a.number - b.number;
	if (sort === 'priority') {
		const byPriority: number = priorityRank(a) - priorityRank(b);
		if (byPriority) return byPriority;
	}
	return b.number - a.number;
};

// Who is working on a report: one person, or one developer's agent (FeedbackReportClaim).
export interface ReportHolder {
	// this exact claim (who, which device and session, since when): a take-over confirmed for one
	// claim never replaces another
	key: string
	uid: string
	name: string | null // null on a report from before claims were kept: look the person up
	kind: FeedbackReportAuthorKind
	agent: string | null // the agent's own name ("Claude Code"); null for a person
	since: Date | null
	assignedBy: string | null // User.id of the admin who assigned it to them; null when they claimed it themself
}

// Its claim, or, on a report from before claims were kept, its owner as a person.
export const currentHolder = (report: FeedbackReport): ReportHolder | null => {
	const claim = report.claim;
	if (claim?.uid) {
		return {
			key: [claim.kind, claim.uid, claim.deviceId || '', claim.session || '', claim.claimedAt ? claim.claimedAt.toMillis() : ''].join('|'),
			uid: claim.uid,
			name: claim.name || null,
			kind: claim.kind === 'agent' ? 'agent' : 'human',
			agent: claim.agent || null,
			since: claim.claimedAt ? claim.claimedAt.toDate() : null,
			assignedBy: claim.assignedBy && claim.assignedBy !== claim.uid ? claim.assignedBy : null,
		};
	}
	const owner: string | null | undefined = report.triage?.owner;
	return owner ? { key: `owner|${owner}`, uid: owner, name: null, kind: 'human', agent: null, since: null, assignedBy: null } : null;
};

// Held by someone other than this person: another admin, or any agent (their own included, so
// taking over from it is a choice they make, not a side effect).
export const heldByOther = (report: FeedbackReport, uid: string): ReportHolder | null => {
	const holder: ReportHolder | null = currentHolder(report);
	return holder && (holder.uid !== uid || holder.kind === 'agent') ? holder : null;
};

// What the take-over confirmation says confirming does to the claim it replaces (`from`): this
// admin (`uid`) claiming it ('claim'), or claiming it and starting work ('progress'), or assigning
// it to `assignee` ('assign'). A developer's agents work on any claim that developer holds as a
// person, assigned ones included, so an agent whose developer becomes the holder isn't stopped:
// only disconnecting it in Connected Devices stops it. Any other new holder stops the agent.
export const takeoverOutcomeText = (
	then: 'claim' | 'progress' | 'assign',
	from: ReportHolder,
	uid: string,
	assignee: { uid: string; name: string } | null = null,
): string => {
	// who holds it once confirmed: the admin it's assigned to, or this admin
	const to: { uid: string; name: string } | null = then === 'assign' ? assignee : null;
	const agent: boolean = from.kind === 'agent';
	const outcome: string = to
		? `Assigning it to ${to.name} takes it from ${agent ? 'the agent' : 'them'}`
		: `Taking it over makes it yours${then === 'progress' ? ' and starts work on it' : ''}`;
	if (!agent) return outcome;
	if (from.uid !== (to ? to.uid : uid)) return `${outcome}. The agent is stopped from changing it, and is told who has it`;
	return to
		? `${outcome}. Their agents can still work on it, this one included, until they disconnect it in Connected Devices`
		: `${outcome}. Your agents can still work on it, this one included: to stop it, disconnect it in Connected Devices`;
};

export interface FeedbackThread {
	comment: FeedbackReportComment // a top-level comment or status line
	replies: FeedbackReportComment[] // oldest first
}

// The comments as threads: each top-level one with its replies, both in the order given (oldest
// first). A reply whose parent isn't there (it shouldn't happen) stands on its own.
export const threadComments = (comments: FeedbackReportComment[]): FeedbackThread[] => {
	const ids: Set<string> = new Set(comments.map((comment) => comment.id));
	// a Map, not an object: a comment's id can be any string, 'constructor' too
	const replies: Map<string, FeedbackReportComment[]> = new Map();
	comments.forEach((comment) => {
		const parentId: string | null | undefined = comment.parentId;
		if (!parentId || !ids.has(parentId)) return;
		const list: FeedbackReportComment[] | undefined = replies.get(parentId);
		if (list) list.push(comment);
		else replies.set(parentId, [comment]);
	});
	return comments
		.filter((comment) => !comment.parentId || !ids.has(comment.parentId))
		.map((comment) => ({ comment, replies: replies.get(comment.id) || [] }));
};

// What stays in the comment box once `sent` has posted: the box is locked while it sends, but a
// reply whose parent was deleted meanwhile is moved into it, after what was sent.
export const draftAfterSend = (draft: string, sent: string): string =>
	(draft.startsWith(sent) ? draft.slice(sent.length).replace(/^\s+/, '') : draft);

// Copy for agent's snackbar: success, or every way the copy fell short.
export const copiedMessage = (
	number: number,
	problems: { links: string, commentsFailed: boolean, commentsCached: boolean },
): { ok: boolean, warnOnly: boolean, message: string } => {
	const n: string = `#${number}`;
	const comments: string = problems.commentsFailed
		? 'Its comments could not be loaded.'
		: (problems.commentsCached ? 'Its comments may be out of date: the server hasn\'t answered yet.' : '');
	if (!problems.links && !comments) return { ok: true, warnOnly: false, message: `Copied ${n} for your agent. Its links work for an hour.` };
	return {
		ok: false,
		warnOnly: !problems.links && !problems.commentsFailed,
		message: [problems.links ? `Copied ${n} without links to its files. ${problems.links}` : `Copied ${n}.`, comments].filter(Boolean).join(' '),
	};
};

// The length rule for a comment, reply, edit or status note. It counts what is saved, the text
// trimmed, so it agrees with the button that sends it.
export const commentLengthRule = (value: string | null | undefined): boolean | string =>
	(value || '').trim().length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH || `Must be ${FEEDBACK_REPORT_COMMENT_MAX_LENGTH} characters or fewer`;

// A pull request link is kept only as an http(s) URL.
export const isPullRequestUrl = (value: string): boolean => /^https?:\/\/\S+$/.test((value || '').trim()) && value.trim().length <= 500;

// The board's flight recorder, as the board packs it (web/board/src/feedback/report.ts): what the
// page shows of it.
export interface FeedbackStep {
	at: number
	kind: string
	label: string
	detail?: string
}

export interface FeedbackBundle {
	steps: FeedbackStep[] // the last BUNDLE_LIMITS.steps of them
	errors: FeedbackStep[] // the last BUNDLE_LIMITS.errors of them
	stepCount: number // how many the bundle held
	errorCount: number
}

// What the page reads of a bundle at most. Anyone signed in can send one, and a crafted bundle
// can hold 100,000 entries in a few compressed megabytes: drawn in full, they freeze the tab. The
// board's own recorder keeps a few hundred steps, so real reports fit well inside these.
export const BUNDLE_LIMITS = {
	steps: 2000,
	errors: 200,
	label: 500, // characters
	detail: 2000,
	kind: 40,
};

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

const clip = (text: string, max: number): string => (text.length > max ? text.slice(0, max) + '...' : text);

// The readable entries of a list, the last `max` of them, each cut to size.
const readSteps = (value: unknown, max: number): { steps: FeedbackStep[], count: number } => {
	const all: unknown[] = Array.isArray(value) ? value : [];
	const steps: FeedbackStep[] = all.slice(-max)
		.filter((step: unknown): step is Record<string, unknown> => isRecord(step) && typeof step.at === 'number' && typeof step.label === 'string')
		.map((step: Record<string, unknown>) => ({
			at: step.at as number,
			kind: typeof step.kind === 'string' ? clip(step.kind, BUNDLE_LIMITS.kind) : '',
			label: clip(step.label as string, BUNDLE_LIMITS.label),
			...(typeof step.detail === 'string' ? { detail: clip(step.detail, BUNDLE_LIMITS.detail) } : {}),
		}));
	return { steps, count: all.length };
};

export const parseBundle = (json: unknown): FeedbackBundle => {
	const steps = readSteps(isRecord(json) ? json.steps : null, BUNDLE_LIMITS.steps);
	const errors = readSteps(isRecord(json) ? json.errors : null, BUNDLE_LIMITS.errors);
	return { steps: steps.steps, errors: errors.steps, stepCount: steps.count, errorCount: errors.count };
};

// The steps' times, against the moment the report was sent: "-1:05".
export const relativeTime = (at: number, sentAt: number): string => {
	const seconds: number = Math.round((at - sentAt) / 1000);
	const sign: string = seconds < 0 ? '-' : '+';
	const abs: number = Math.abs(seconds);
	return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};

// A stored time as "2026-10-30 17:05 UTC".
const isoMinute = (value: { toMillis (): number } | null | undefined): string =>
	value ? new Date(value.toMillis()).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : 'unknown';

// What getFeedbackFileLinks answers (GetFeedbackFileLinksOnCallResult in firebase/functions/src/types).
export type FeedbackFileLinksResult =
	{ success: true, shots: string[], bundle: string | null, expiresAt: number } |
	{ success: false, message: string };

export interface AgentReportLinks {
	shots: string[] // a link per screenshot, in order; empty when none could be made
	bundle: string | null
	expiresAt: number | null // when the links stop working (ms epoch); null without links
}

// Deleted by its author: it stays in the thread, its words hidden.
export const isCommentDeleted = (comment: FeedbackReportComment): boolean => !!comment.deletedAt;

const WORK: FeedbackReportStatus[] = ['ready_for_agent', 'in_progress'];

// Waiting on a question asked while it was approved work (ready for agent or in progress), so
// answering it sends it back to work, as the feedback API reads it (is_work in restServer
// apiv2/feedback_service.py). A report a person sent back keeps its old approval on the page, so
// the approval alone doesn't say this.
export const askedDuringWork = (report: FeedbackReport, comments: FeedbackReportComment[]): boolean => {
	if (statusOf(report) !== 'needs_human' || !report.triage?.approvedBy) return false;
	const askedFrom: FeedbackReportStatus | null = feedbackReportStatus(report.triage.askedFrom);
	if (askedFrom) return WORK.includes(askedFrom);
	// a question asked before askedFrom was kept: its status line says where from
	for (let i = comments.length - 1; i >= 0; i--) {
		const comment: FeedbackReportComment = comments[i];
		if (comment.kind !== 'status') continue;
		return feedbackReportStatus(comment.to) === 'needs_human' && WORK.includes(feedbackReportStatus(comment.from) as FeedbackReportStatus);
	}
	return false;
};

// The row of a list it's kept in step with, or, on one imported before listRow was kept, what its
// origin says of the row.
export const listRowOf = (report: FeedbackReport): Partial<FeedbackReportListRow> | null => {
	if (report.listRow?.key) return report.listRow;
	if (report.origin?.kind === 'import' && report.origin.externalId) {
		return { list: report.origin.list || '', externalId: report.origin.externalId, externalStatus: report.origin.externalStatus, link: report.origin.link };
	}
	return null;
};

const commentLine = (comment: FeedbackReportComment): string => {
	if (isCommentDeleted(comment)) return `${comment.author.name} (${isoMinute(comment.createdAt)}): (comment deleted)`;
	const edited: string = comment.editedAt ? ', edited' : '';
	if (comment.kind === 'status') {
		const moved: string = `${comment.author.name} moved it from ${statusLabel(comment.from)} to ${statusLabel(comment.to)}`;
		return `${moved} (${isoMinute(comment.createdAt)})${comment.body ? `: ${comment.body}` : ''}`;
	}
	const shots: string = comment.shots?.length ? ` [${comment.shots.length} screenshot${comment.shots.length === 1 ? '' : 's'}: ${comment.shots.map((shot) => shot.path).join(', ')}]` : '';
	return `${comment.author.name} (${isoMinute(comment.createdAt)}${edited}): ${comment.body || '(a screenshot)'}${shots}`;
};

// The report as Markdown, for pasting into a developer's agent session. The reporter's email is
// left out. What a report doesn't have (a board, a build, a browser) is left out too, and one an
// agent filed or imported says who filed it and what its list says of it. The screenshot and
// bundle links are signed for an hour (getFeedbackFileLinks): whoever holds them can read the
// files until then. Without links, the files' storage paths stand in. `names` names the people it
// knows by User.id (who assigned it to its holder); anyone else is named by their id.
export const reportForAgent = (report: FeedbackReport, comments: FeedbackReportComment[], links: AgentReportLinks, names: Record<string, string> = {}): string => {
	const lines: string[] = [];
	const priority: FeedbackReportPriority | null | undefined = report.triage?.priority;
	lines.push(`# Feedback #${report.number}: ${KIND_LABELS[report.kind]} (${statusLabel(report.status)}${priority ? `, ${PRIORITY_LABELS[priority]}` : ''})`);
	lines.push('');
	const kind: FeedbackReportOriginKind | null = originKind(report);
	if (kind) {
		// filed by a developer's agent, or imported from a list: who did it, and what the list says
		const origin: FeedbackReportOrigin | null = report.origin || null;
		const link: string | null = httpLink(origin?.link);
		const who: string = origin?.name || 'a developer\'s agent';
		if (kind === 'import') {
			lines.push(`- Imported: ${isoMinute(report.createdAt)} by ${who}, from ${origin?.list ? `the list "${origin.list}"` : 'a list'}${origin?.externalId ? `, row ${origin.externalId}` : ''}`);
		} else if (kind === 'page') {
			lines.push(`- Filed: ${isoMinute(report.createdAt)} by ${origin?.name || 'an admin'}, on the Feedback page`);
		} else {
			lines.push(`- Filed: ${isoMinute(report.createdAt)} by ${who} (an agent)`);
		}
		if (origin?.reportedBy) lines.push(`- Reported by: ${origin.reportedBy}`);
		if (origin?.reportedAt) lines.push(`- Reported at: ${origin.reportedAt} (as the list gives it)`);
		if (origin?.externalStatus) lines.push(`- Status in the list: ${origin.externalStatus}`);
		if (link) lines.push(`- Link: ${link}`);
	} else {
		lines.push(`- Sent: ${isoMinute(report.createdAt)} by ${isAdminReport(report) ? 'a RunDiffusion admin' : 'a user'}${report.reporter?.companyName ? ` at ${report.reporter.companyName}` : ''}, ${arrival(report).via}`);
	}
	// what isn't known is left out: a report an agent filed or imported has no board, and may have
	// no build or browser (null, or an empty placeholder)
	if (hasBoard(report.board)) lines.push(`- Board: "${report.board.title || 'Untitled'}" (${report.board.id})`);
	if (hasBuild(report.build)) lines.push(`- Build: ${report.build.version || 'unknown'}${report.build.commit ? ` (${report.build.commit})` : ''}`);
	if (hasEnv(report.env)) {
		const screen: string = hasViewport(report.env) ? `, ${report.env.viewport.w}x${report.env.viewport.h} at ${report.env.dpr}x` : '';
		lines.push(`- Browser: ${report.env.browser || 'unknown'} on ${report.env.os || 'unknown'}${screen}${report.env.gpu ? `, GPU ${report.env.gpu}` : ''}`);
	}
	const holder: ReportHolder | null = currentHolder(report);
	if (holder) {
		const assigned: string = holder.assignedBy ? ` (assigned by ${names[holder.assignedBy] || holder.assignedBy})` : '';
		lines.push(`- Claimed by: ${holder.name || holder.uid}${holder.kind === 'agent' ? ' (an agent)' : ''}${assigned}${holder.since ? ` since ${holder.since.toISOString().slice(0, 16).replace('T', ' ')} UTC` : ''}`);
	}
	const row: Partial<FeedbackReportListRow> | null = listRowOf(report);
	if (row && kind !== 'import') lines.push(`- Kept in step with row ${row.externalId} of the list "${row.list}"${row.externalStatus ? `, which says ${row.externalStatus}` : ''}`);
	const tags: string[] = tagsOf(report);
	if (tags.length) lines.push(`- Tags: ${tags.join(', ')}`);
	if (report.triage?.pr) lines.push(`- Pull request: ${report.triage.pr}${report.triage.mergedAt ? ` (merged ${isoMinute(report.triage.mergedAt)})` : ''}`);
	if (report.triage?.duplicateOf != null) lines.push(`- Duplicate of #${report.triage.duplicateOf}`);
	lines.push('');
	lines.push(`## ${KIND_QUESTIONS[report.kind].happened}`);
	lines.push(report.happened);
	if (report.expected) {
		lines.push('');
		lines.push(`## ${KIND_QUESTIONS[report.kind].expected}`);
		lines.push(report.expected);
	}
	if ((report.shots.length || report.bundle) && links.expiresAt) {
		lines.push('');
		lines.push(`The links below work until ${isoMinute({ toMillis: () => links.expiresAt as number })}, an hour from when this was copied.`);
	}
	if (report.shots.length) {
		lines.push('');
		lines.push('## Screenshots');
		report.shots.forEach((shot, i) => {
			const where: string = [
				`${shot.w}x${shot.h}`,
				shot.camera ? `camera ${Math.round(shot.camera.x)},${Math.round(shot.camera.y)} zoom ${shot.camera.zoom}` : '',
				shot.selection.length ? `selected ${shot.selection.join(', ')}` : '',
				shot.panels.length ? `open: ${shot.panels.join(', ')}` : '',
			].filter(Boolean).join('; ');
			lines.push(`${i + 1}. ${links.shots[i] || shot.path} (${where})`);
		});
	}
	if (report.bundle) {
		lines.push('');
		lines.push('## Recent activity');
		lines.push(`${report.counts.steps} steps and ${report.counts.errors} errors, in ${report.bundle.gzip ? 'a gzipped' : 'a'} JSON bundle (steps, errors, frame times, board state, element snapshot):`);
		lines.push(links.bundle || report.bundle.path);
	}
	if (comments.length) {
		lines.push('');
		lines.push('## Comments and history');
		threadComments(comments).forEach((thread) => {
			lines.push(`- ${commentLine(thread.comment)}`);
			thread.replies.forEach((reply) => lines.push(`  - ${commentLine(reply)}`));
		});
	}
	return lines.join('\n');
};
